import type { ProjectSummary, RegulationScope, ScanStatus } from '../types';

export type ProjectWorkspaceSort =
  | 'updated_desc'
  | 'name_asc'
  | 'gaps_desc'
  | 'tasks_desc';

export interface ProjectWorkspaceFilters {
  search?: string;
  status?: ScanStatus | 'all';
  scope?: RegulationScope | 'all';
  sort?: ProjectWorkspaceSort;
}

export function projectWorkspaceCardPresentation(project: ProjectSummary): {
  showUnnamedProjectPrompt: boolean;
  showUnresolvedGaps: boolean;
  rescanMessage?: string;
} {
  const failed = project.latestScanStatus === 'error';
  return {
    showUnnamedProjectPrompt: project.name === 'Unnamed project',
    showUnresolvedGaps: !failed,
    ...(failed && {
      rescanMessage: 'Analysis failed. Open the workspace to rescan.',
    }),
  };
}

export function filterAndSortProjects(
  projects: ProjectSummary[],
  filters: ProjectWorkspaceFilters
): ProjectSummary[] {
  const search = filters.search?.trim().toLocaleLowerCase() ?? '';
  const filtered = projects.filter(project => {
    const matchesSearch =
      !search || project.name.toLocaleLowerCase().includes(search);
    const matchesStatus =
      !filters.status ||
      filters.status === 'all' ||
      project.latestScanStatus === filters.status;
    const matchesScope =
      !filters.scope ||
      filters.scope === 'all' ||
      project.regulationScope === filters.scope;
    return matchesSearch && matchesStatus && matchesScope;
  });

  return filtered.sort(projectComparator(filters.sort ?? 'updated_desc'));
}

function projectComparator(sort: ProjectWorkspaceSort) {
  return (left: ProjectSummary, right: ProjectSummary): number => {
    const compareName = left.name.localeCompare(right.name);
    if (sort === 'name_asc')
      return compareName || left.id.localeCompare(right.id);
    if (sort === 'gaps_desc') {
      return (
        right.unresolvedGapCount - left.unresolvedGapCount ||
        compareName ||
        left.id.localeCompare(right.id)
      );
    }
    if (sort === 'tasks_desc') {
      return (
        right.openTaskCount - left.openTaskCount ||
        compareName ||
        left.id.localeCompare(right.id)
      );
    }

    return (
      projectTimestamp(right.lastUpdatedAt) -
        projectTimestamp(left.lastUpdatedAt) ||
      compareName ||
      left.id.localeCompare(right.id)
    );
  };
}

function projectTimestamp(value: string): number {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}
